import React, { useState, useMemo } from 'react';
import { Key, Plus, RotateCw, CheckCircle2, Ban, X } from 'lucide-react';
import { useSecurityStore, MaskingRule } from '../../../store/securityStore';
import { useAuthStore } from '../../../store/authStore';
import { RowActions } from '../../../components/RowActions';
import { Button } from '../../../components/Button';
import toast from 'react-hot-toast';

export function DataProtectionSubTab() {
  const { 
    encryptionSettings, encryptionKeys, maskingRules, dlpRules, dlpIncidents,
    securityAuditLogs, updateEncryptionSettings, createEncryptionKey, 
    rotateEncryptionKey, disableEncryptionKey, deleteEncryptionKey, 
    createMaskingRule, updateMaskingRule, deleteMaskingRule, 
    toggleMaskingRule, updateDLPRule, resolveDLPIncident 
  } = useSecurityStore();
  const { user } = useAuthStore();

  const isSuperAdmin = (user?.roles || []).some((r: any) => 
    typeof r === 'string' 
      ? r.toLowerCase().includes('admin') || r.toLowerCase().includes('super')
      : r.code === 'super_admin' || r.name === 'Super Admin'
  );

  const [activeSubTab, setActiveSubTab] = useState<'encryption' | 'masking' | 'dlp' | 'audit'>('encryption');

  // Key Modal State
  const [isKeyModalOpen, setIsKeyModalOpen] = useState(false);
  const [keyForm, setKeyForm] = useState({
    keyId: '',
    algorithm: 'AES-256-GCM' as 'AES-256-GCM' | 'RSA-4096' | 'ChaCha20-Poly1305',
    scope: 'database' as 'database' | 'storage' | 'tokens' | 'backups',
    rotationScheduleDays: 90,
  });

  // Masking Rule Modal State
  const [isMaskModalOpen, setIsMaskModalOpen] = useState(false);
  const [editingMaskRule, setEditingMaskRule] = useState<MaskingRule | null>(null);
  const [maskForm, setMaskForm] = useState({
    columnName: '',
    tableName: '',
    maskType: 'partial' as 'full' | 'partial' | 'hash' | 'tokenize',
    allowedRoles: ['super_admin'],
    sampleOriginal: '4532-8921-9923-1124',
    sampleMasked: '•••• •••• •••• 1124',
  });

  // Live Masking Preview Role Toggle
  const [previewRole, setPreviewRole] = useState<'employee' | 'hr_admin' | 'super_admin'>('employee');

  const handleOpenKeyModal = () => {
    setKeyForm({
      keyId: `KMS-CUSTOM-${Date.now().toString().slice(-4)}`,
      algorithm: 'AES-256-GCM',
      scope: 'database',
      rotationScheduleDays: 90,
    });
    setIsKeyModalOpen(true);
  };

  const handleSaveKey = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!keyForm.keyId.trim()) {
      toast.error('Key identifier is required');
      return;
    }
    await createEncryptionKey({
      keyId: keyForm.keyId.trim(),
      algorithm: keyForm.algorithm,
      scope: keyForm.scope,
      status: 'active',
      rotationScheduleDays: Number(keyForm.rotationScheduleDays),
    });
    setIsKeyModalOpen(false);
  };

  const handleOpenMaskModal = (rule?: MaskingRule) => {
    if (rule) {
      setEditingMaskRule(rule);
      setMaskForm({
        columnName: rule.columnName,
        tableName: rule.tableName,
        maskType: rule.maskType,
        allowedRoles: [...rule.allowedRoles],
        sampleOriginal: rule.sampleOriginal,
        sampleMasked: rule.sampleMasked,
      });
    } else {
      setEditingMaskRule(null);
      setMaskForm({
        columnName: '',
        tableName: 'billing_payment_methods',
        maskType: 'partial',
        allowedRoles: ['super_admin'],
        sampleOriginal: '4532-8921-9923-1124',
        sampleMasked: '•••• •••• •••• 1124',
      });
    }
    setIsMaskModalOpen(true);
  };

  const handleSaveMaskRule = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!maskForm.columnName.trim()) {
      toast.error('Column name is required');
      return;
    }

    if (editingMaskRule) {
      await updateMaskingRule({
        ...editingMaskRule,
        columnName: maskForm.columnName.trim(),
        tableName: maskForm.tableName,
        maskType: maskForm.maskType,
        allowedRoles: maskForm.allowedRoles,
        sampleOriginal: maskForm.sampleOriginal,
        sampleMasked: maskForm.sampleMasked,
      });
    } else {
      await createMaskingRule({
        columnName: maskForm.columnName.trim(),
        tableName: maskForm.tableName,
        maskType: maskForm.maskType,
        allowedRoles: maskForm.allowedRoles,
        sampleOriginal: maskForm.sampleOriginal,
        sampleMasked: maskForm.sampleMasked,
        status: 'active',
      });
    }
    setIsMaskModalOpen(false);
  };

  const dataAuditLogs = useMemo(() => {
    return securityAuditLogs.filter((l) => l.section === 'data_protection');
  }, [securityAuditLogs]);

  return (
    <div className="space-y-6">
      {/* Sub-tab Navigation */}
      <div className="flex flex-wrap items-center gap-2 border-b border-border pb-3">
        {[
          { id: 'encryption', label: `Encryption & KMS (${encryptionKeys.length} Keys)` },
          { id: 'masking', label: `Data Masking Rules (${maskingRules.length})` },
          { id: 'dlp', label: `DLP & Incidents (${dlpIncidents.length} Events)` },
          { id: 'audit', label: `Data Protection Audit (${dataAuditLogs.length})` },
        ].map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveSubTab(tab.id as any)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
              activeSubTab === tab.id
                ? 'bg-primary text-primary-foreground shadow-xs'
                : 'bg-card border border-border text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* 1. ENCRYPTION & KMS KEYS */}
      {activeSubTab === 'encryption' && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-3">
            <div>
              <h2 className="font-display text-sm font-semibold text-foreground">
                Cryptographic Key Management &amp; In-Transit TLS
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Hardware Security Module (HSM) key rotation, field-level encryption, and communication ciphers.
              </p>
            </div>
            {isSuperAdmin && (
              <Button
                variant="primary"
                size="sm"
                onClick={handleOpenKeyModal}
                className="gap-1.5 h-8.5 shrink-0"
              >
                <Plus className="size-3.5" />
                <span>Create Encryption Key</span>
              </Button>
            )}
          </div>

          {/* Cryptographic Overview Row */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="p-4 rounded-xl border border-border bg-card shadow-xs flex items-center justify-between">
              <div>
                <span className="text-3xs font-semibold uppercase text-muted-foreground block">At-Rest Protection</span>
                <p className="font-semibold text-xs text-foreground mt-0.5">AES-256-GCM / HSM Protected</p>
              </div>
              <span className="px-2 py-0.5 rounded-full text-3xs font-semibold bg-success/10 text-success-strong border border-success/30">
                Active
              </span>
            </div>

            <div className="p-4 rounded-xl border border-border bg-card shadow-xs flex items-center justify-between">
              <div>
                <span className="text-3xs font-semibold uppercase text-muted-foreground block">In-Transit TLS Minimum</span>
                <p className="font-semibold text-xs text-foreground mt-0.5">{encryptionSettings.tlsMinVersion}</p>
              </div>
              <select
                disabled={!isSuperAdmin}
                value={encryptionSettings.tlsMinVersion}
                onChange={(e) => updateEncryptionSettings({ tlsMinVersion: e.target.value as any })}
                className="h-7 px-2 rounded-lg border border-border bg-background text-xs font-semibold text-foreground outline-none cursor-pointer"
              >
                <option value="TLS 1.2">TLS 1.2</option>
                <option value="TLS 1.3">TLS 1.3 (Recommended)</option>
              </select>
            </div>

            <div className="p-4 rounded-xl border border-border bg-card shadow-xs flex items-center justify-between">
              <div>
                <span className="text-3xs font-semibold uppercase text-muted-foreground block">Field-Level Protection</span>
                <p className="font-semibold text-xs text-foreground mt-0.5">{encryptionSettings.fieldLevelEncryptedColumns.length} Columns</p>
              </div>
              <span className="px-2 py-0.5 rounded-full text-3xs font-semibold bg-primary/10 text-primary border border-primary/20">
                Encrypted
              </span>
            </div>
          </div>

          {/* KMS Keys Table */}
          <div className="rounded-2xl border border-border bg-card overflow-hidden shadow-xs">
            <div className="p-4 border-b border-border bg-muted/10 flex items-center justify-between">
              <h3 className="font-semibold text-xs text-foreground">Cryptographic Keys Inventory</h3>
              <span className="text-xs font-semibold text-muted-foreground">{encryptionKeys.length} keys</span>
            </div>

            <div className="divide-y divide-border">
              {encryptionKeys.map((key) => (
                <div key={key.id} className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Key className="size-3.5 text-primary" />
                      <span className="font-mono font-semibold text-foreground">{key.keyId}</span>
                      <span className="px-2 py-0.2 rounded bg-muted text-foreground border border-border font-mono text-3xs">
                        {key.algorithm}
                      </span>
                      <span
                        className={`px-2 py-0.2 rounded-full font-semibold text-3xs uppercase tracking-wider ${
                          key.status === 'active'
                            ? 'bg-success/10 text-success-strong border border-success/30'
                            : 'bg-muted text-muted-foreground border border-border'
                        }`}
                      >
                        {key.status}
                      </span>
                    </div>
                    <p className="text-3xs text-muted-foreground">
                      Scope: <strong className="text-foreground">{key.scope}</strong> · Rotation schedule: every {key.rotationScheduleDays} days · Last rotated: {new Date(key.lastRotatedAt).toLocaleDateString()}
                    </p>
                  </div>

                  <div className="flex items-center gap-2 self-start sm:self-auto">
                    {isSuperAdmin && key.status === 'active' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => rotateEncryptionKey(key.id)}
                        className="h-7.5 text-xs gap-1"
                        title="Generate new cryptographic version"
                      >
                        <RotateCw className="size-3" />
                        <span>Rotate</span>
                      </Button>
                    )}

                    <RowActions
                      entityName={key.keyId}
                      deleteTitle="Delete Encryption Key"
                      deleteMessage={`Are you sure you want to delete ${key.keyId}? Any data encrypted solely with this key will become irrecoverable.`}
                      onDelete={isSuperAdmin && key.status !== 'active' ? () => deleteEncryptionKey(key.id) : undefined}
                      customActions={
                        isSuperAdmin && key.status === 'active'
                          ? [
                              {
                                label: 'Rotate Key Version',
                                icon: <RotateCw className="size-3.5" />,
                                onClick: () => rotateEncryptionKey(key.id),
                              },
                              {
                                label: 'Disable / Retire Key',
                                icon: <Ban className="size-3.5" />,
                                onClick: () => disableEncryptionKey(key.id),
                              },
                            ]
                          : []
                      }
                      readOnly={!isSuperAdmin}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* 2. DATA MASKING RULES WITH LIVE PREVIEW */}
      {activeSubTab === 'masking' && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-3">
            <div>
              <h2 className="font-display text-sm font-semibold text-foreground">
                Dynamic Data Masking Rules &amp; Column Tokenization
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Automatically obscure sensitive PII, salary figures, and payment card details based on viewer role.
              </p>
            </div>
            {isSuperAdmin && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => handleOpenMaskModal()}
                className="gap-1.5 h-8.5 shrink-0"
              >
                <Plus className="size-3.5" />
                <span>Add Masking Rule</span>
              </Button>
            )}
          </div>

          {/* Interactive Live Preview Box */}
          <div className="p-5 rounded-2xl border border-border bg-card shadow-xs space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-border">
              <div>
                <h3 className="font-semibold text-xs text-foreground">Live Data Masking Visualizer</h3>
                <p className="text-3xs text-muted-foreground">Select viewer role to simulate on-the-fly column redaction</p>
              </div>

              <div className="flex items-center gap-1.5 bg-muted/40 p-1 rounded-xl border border-border">
                <span className="text-3xs font-semibold uppercase text-muted-foreground px-2">View As:</span>
                {(['employee', 'hr_admin', 'super_admin'] as const).map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setPreviewRole(r)}
                    className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                      previewRole === r
                        ? 'bg-primary text-primary-foreground shadow-2xs'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {r === 'employee' ? 'Employee' : r === 'hr_admin' ? 'HR Admin' : 'Super Admin'}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
              {maskingRules.map((rule) => {
                const canSeeOriginal = rule.allowedRoles.includes(previewRole);
                const displayVal = canSeeOriginal ? rule.sampleOriginal : rule.sampleMasked;

                return (
                  <div key={rule.id} className="p-3.5 rounded-xl border border-border bg-muted/20 space-y-1.5">
                    <span className="text-3xs font-mono font-semibold text-muted-foreground block truncate">
                      {rule.tableName}.{rule.columnName}
                    </span>
                    <p className="text-xs font-mono font-bold text-foreground">
                      {displayVal}
                    </p>
                    <span className={`inline-block px-1.5 py-0.2 rounded text-3xs font-semibold ${
                      canSeeOriginal
                        ? 'bg-success/10 text-success-strong'
                        : 'bg-amber-500/10 text-amber-600 dark:text-amber-400'
                    }`}>
                      {canSeeOriginal ? 'Unmasked (Clearance)' : `Masked (${rule.maskType})`}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Rules Table */}
          <div className="rounded-2xl border border-border bg-card overflow-hidden shadow-xs">
            <div className="p-4 border-b border-border bg-muted/10 flex items-center justify-between">
              <h3 className="font-semibold text-xs text-foreground">Active Masking Policies</h3>
              <span className="text-xs font-semibold text-muted-foreground">{maskingRules.length} rules</span>
            </div>

            <div className="divide-y divide-border">
              {maskingRules.map((rule) => (
                <div key={rule.id} className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-semibold text-foreground">{rule.tableName}.{rule.columnName}</span>
                      <span className="px-2 py-0.2 rounded bg-muted text-foreground border border-border font-mono text-3xs uppercase">
                        Type: {rule.maskType}
                      </span>
                      <span
                        className={`px-2 py-0.2 rounded-full font-semibold text-3xs uppercase tracking-wider ${
                          rule.status === 'active'
                            ? 'bg-success/10 text-success-strong border border-success/30'
                            : 'bg-muted text-muted-foreground border border-border'
                        }`}
                      >
                        {rule.status}
                      </span>
                    </div>
                    <p className="text-3xs text-muted-foreground">
                      Allowed unmasked roles: <strong className="text-foreground">{rule.allowedRoles.join(', ')}</strong> · Sample Output: <code className="bg-muted px-1 rounded">{rule.sampleMasked}</code>
                    </p>
                  </div>

                  <RowActions
                    entityName={`${rule.tableName}.${rule.columnName}`}
                    onEdit={isSuperAdmin ? () => handleOpenMaskModal(rule) : undefined}
                    onToggleStatus={isSuperAdmin ? () => toggleMaskingRule(rule.id) : undefined}
                    isStatusActive={rule.status === 'active'}
                    onDelete={isSuperAdmin ? () => deleteMaskingRule(rule.id) : undefined}
                    onViewDetails={() => handleOpenMaskModal(rule)}
                    readOnly={!isSuperAdmin}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* 3. DLP (DATA LOSS PREVENTION) & INCIDENTS */}
      {activeSubTab === 'dlp' && (
        <div className="space-y-6">
          <div>
            <h2 className="font-display text-sm font-semibold text-foreground">
              Data Loss Prevention (DLP) Policies &amp; Inspection Logs
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Inspect file uploads, exports, outbound emails, and API responses for PCI, PII, and exposed secret patterns.
            </p>
          </div>

          {/* Rules grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {dlpRules.map((rule) => (
              <div key={rule.id} className="p-4 rounded-xl border border-border bg-card shadow-xs space-y-3">
                <div className="flex items-center justify-between">
                  <span className={`px-2 py-0.5 rounded-full font-bold text-3xs uppercase tracking-wider ${
                    rule.severity === 'critical'
                      ? 'bg-destructive/10 text-destructive border border-destructive/20'
                      : 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/30'
                  }`}>
                    {rule.severity}
                  </span>

                  <input
                    type="checkbox"
                    disabled={!isSuperAdmin}
                    checked={rule.enabled}
                    onChange={(e) => updateDLPRule({ ...rule, enabled: e.target.checked })}
                    className="size-4 rounded border-border text-primary focus:ring-primary cursor-pointer"
                  />
                </div>

                <div>
                  <h4 className="font-semibold text-xs text-foreground">{rule.name}</h4>
                  <p className="text-3xs text-muted-foreground mt-0.5">
                    Action: <strong className="text-foreground uppercase">{rule.action}</strong> · Scope: {rule.scope.join(', ')}
                  </p>
                </div>

                <div className="text-3xs text-muted-foreground pt-1 border-t border-border flex justify-between">
                  <span>Inspection Matches:</span>
                  <span className="font-bold text-foreground">{rule.matchesCount} events</span>
                </div>
              </div>
            ))}
          </div>

          {/* Incidents Table */}
          <div className="rounded-2xl border border-border bg-card overflow-hidden shadow-xs">
            <div className="p-4 border-b border-border bg-muted/10 flex items-center justify-between">
              <h3 className="font-semibold text-xs text-foreground">Recent DLP Violation Incidents</h3>
              <span className="text-xs font-semibold text-muted-foreground">{dlpIncidents.length} events logged</span>
            </div>

            <div className="divide-y divide-border">
              {dlpIncidents.map((inc) => (
                <div key={inc.id} className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-foreground">{inc.ruleName}</span>
                      <span className={`px-2 py-0.2 rounded-full font-bold text-3xs uppercase ${
                        inc.status === 'open'
                          ? 'bg-destructive/10 text-destructive border border-destructive/20'
                          : 'bg-success/10 text-success-strong border border-success/30'
                      }`}>
                        {inc.status}
                      </span>
                    </div>
                    <p className="text-3xs text-muted-foreground">
                      User: <strong className="text-foreground">{inc.userEmail}</strong> · Scope: {inc.scope} · Action: {inc.actionTaken}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">{inc.details}</p>
                  </div>

                  <div className="flex items-center gap-2 self-start sm:self-auto shrink-0">
                    <span className="text-3xs text-muted-foreground mr-2">
                      {new Date(inc.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                    {isSuperAdmin && inc.status !== 'resolved' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => resolveDLPIncident(inc.id)}
                        className="h-7 text-3xs gap-1"
                      >
                        <CheckCircle2 className="size-3" />
                        <span>Resolve</span>
                      </Button>
                    )}

                    <RowActions
                      entityName={`DLP Event: ${inc.ruleName}`}
                      onViewDetails={() => toast.success(`Details: ${inc.details}`)}
                      customActions={
                        isSuperAdmin && inc.status !== 'resolved'
                          ? [
                              {
                                label: 'Mark Resolved',
                                icon: <CheckCircle2 className="size-3.5 text-success" />,
                                onClick: () => resolveDLPIncident(inc.id),
                              },
                            ]
                          : []
                      }
                      readOnly={!isSuperAdmin}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* 4. AUDIT TRAIL TAB */}
      {activeSubTab === 'audit' && (
        <div className="rounded-2xl border border-border bg-card overflow-hidden shadow-xs">
          <div className="p-4 border-b border-border bg-muted/10 flex items-center justify-between">
            <h3 className="font-semibold text-xs text-foreground">Data Protection Governance Audit Trail</h3>
            <span className="text-xs font-semibold text-muted-foreground">{dataAuditLogs.length} events</span>
          </div>

          <div className="divide-y divide-border max-h-[500px] overflow-y-auto">
            {dataAuditLogs.length === 0 ? (
              <div className="py-10 text-center text-muted-foreground text-xs">No data protection audit logs</div>
            ) : (
              dataAuditLogs.map((log) => (
                <div key={log.id} className="p-3.5 hover:bg-muted/10 text-xs">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold text-foreground">{log.action}</p>
                      <p className="text-3xs text-muted-foreground mt-0.5">
                        Target: <span className="text-primary font-semibold">{log.target}</span> · Performed by {log.performedBy}
                      </p>
                    </div>
                    <span className="text-3xs text-muted-foreground shrink-0">
                      {new Date(log.timestamp).toLocaleString()}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Add Key Drawer (Opens on Right) */}
      {isKeyModalOpen && (
        <div
          className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-2xs animate-in fade-in"
          onClick={() => setIsKeyModalOpen(false)}
        >
          <div
            className="w-full max-w-md bg-card border-l border-border h-full flex flex-col justify-between p-6 shadow-2xl animate-in slide-in-from-right duration-200 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between pb-3 border-b border-border">
              <h2 className="font-display text-base font-semibold text-foreground">Create Encryption Key</h2>
              <button
                type="button"
                onClick={() => setIsKeyModalOpen(false)}
                className="size-8 rounded-lg hover:bg-muted text-muted-foreground flex items-center justify-center transition-colors cursor-pointer"
              >
                <X className="size-4" />
              </button>
            </div>

            <form onSubmit={handleSaveKey} className="space-y-3.5">
              <div>
                <label className="text-xs font-semibold text-foreground block mb-1">Key Identifier</label>
                <input
                  type="text"
                  value={keyForm.keyId}
                  onChange={(e) => setKeyForm({ ...keyForm, keyId: e.target.value })}
                  className="w-full h-8.5 px-3 rounded-lg border border-border bg-background text-xs font-mono text-foreground outline-none focus:border-primary"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-foreground block mb-1">Cipher Algorithm</label>
                  <select
                    value={keyForm.algorithm}
                    onChange={(e) => setKeyForm({ ...keyForm, algorithm: e.target.value as any })}
                    className="w-full h-8.5 px-2 rounded-lg border border-border bg-background text-xs text-foreground outline-none focus:border-primary cursor-pointer font-mono"
                  >
                    <option value="AES-256-GCM">AES-256-GCM</option>
                    <option value="RSA-4096">RSA-4096</option>
                    <option value="ChaCha20-Poly1305">ChaCha20-Poly1305</option>
                  </select>
                </div>

                <div>
                  <label className="text-xs font-semibold text-foreground block mb-1">Key Scope</label>
                  <select
                    value={keyForm.scope}
                    onChange={(e) => setKeyForm({ ...keyForm, scope: e.target.value as any })}
                    className="w-full h-8.5 px-2 rounded-lg border border-border bg-background text-xs text-foreground outline-none focus:border-primary cursor-pointer"
                  >
                    <option value="database">Database Fields</option>
                    <option value="storage">File Storage</option>
                    <option value="tokens">JWT / Tokens</option>
                    <option value="backups">Backups Vault</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-foreground block mb-1">Rotation Schedule</label>
                <select
                  value={keyForm.rotationScheduleDays}
                  onChange={(e) => setKeyForm({ ...keyForm, rotationScheduleDays: Number(e.target.value) })}
                  className="w-full h-8.5 px-2.5 rounded-lg border border-border bg-background text-xs text-foreground outline-none focus:border-primary cursor-pointer"
                >
                  <option value="90">Every 90 Days</option>
                  <option value="180">Every 180 Days</option>
                  <option value="365">Every 365 Days</option>
                </select>
              </div>

              <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-border">
                <Button variant="outline" size="sm" type="button" onClick={() => setIsKeyModalOpen(false)}>
                  Cancel
                </Button>
                <Button variant="primary" size="sm" type="submit">
                  Generate Key
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add / Edit Masking Rule Drawer (Opens on Right) */}
      {isMaskModalOpen && (
        <div
          className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-2xs animate-in fade-in"
          onClick={() => setIsMaskModalOpen(false)}
        >
          <div
            className="w-full max-w-md bg-card border-l border-border h-full flex flex-col justify-between p-6 shadow-2xl animate-in slide-in-from-right duration-200 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between pb-3 border-b border-border">
              <h2 className="font-display text-base font-semibold text-foreground">
                {editingMaskRule ? 'Edit Masking Rule' : 'Add Column Masking Rule'}
              </h2>
              <button
                type="button"
                onClick={() => setIsMaskModalOpen(false)}
                className="size-8 rounded-lg hover:bg-muted text-muted-foreground flex items-center justify-center transition-colors cursor-pointer"
              >
                <X className="size-4" />
              </button>
            </div>

            <form onSubmit={handleSaveMaskRule} className="space-y-3.5">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-foreground block mb-1">Table Name</label>
                  <input
                    type="text"
                    value={maskForm.tableName}
                    onChange={(e) => setMaskForm({ ...maskForm, tableName: e.target.value })}
                    className="w-full h-8.5 px-3 rounded-lg border border-border bg-background text-xs font-mono text-foreground outline-none focus:border-primary"
                    placeholder="people_employees"
                    required
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-foreground block mb-1">Column Name</label>
                  <input
                    type="text"
                    value={maskForm.columnName}
                    onChange={(e) => setMaskForm({ ...maskForm, columnName: e.target.value })}
                    className="w-full h-8.5 px-3 rounded-lg border border-border bg-background text-xs font-mono text-foreground outline-none focus:border-primary"
                    placeholder="national_id_ssn"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-foreground block mb-1">Masking Algorithm</label>
                <select
                  value={maskForm.maskType}
                  onChange={(e) => setMaskForm({ ...maskForm, maskType: e.target.value as any })}
                  className="w-full h-8.5 px-2.5 rounded-lg border border-border bg-background text-xs text-foreground outline-none focus:border-primary cursor-pointer"
                >
                  <option value="partial">Partial Mask (Keep last 4 characters visible)</option>
                  <option value="full">Full Mask (Replace all characters with •••••)</option>
                  <option value="hash">Hash Mask (SHA-256 One-Way Digest)</option>
                  <option value="tokenize">Tokenize (Surrogate Random Token)</option>
                </select>
              </div>

              <div>
                <label className="text-xs font-semibold text-foreground block mb-1">Sample Raw Value</label>
                <input
                  type="text"
                  value={maskForm.sampleOriginal}
                  onChange={(e) => setMaskForm({ ...maskForm, sampleOriginal: e.target.value })}
                  className="w-full h-8.5 px-3 rounded-lg border border-border bg-background text-xs font-mono text-foreground outline-none"
                />
              </div>

              <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-border">
                <Button variant="outline" size="sm" type="button" onClick={() => setIsMaskModalOpen(false)}>
                  Cancel
                </Button>
                <Button variant="primary" size="sm" type="submit">
                  Save Rule
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
